import { normalize } from 'normalizr'
import { Schemas } from 'redux-modules/schema'
import { updateEntities, deleteEntity } from 'redux-modules/entity'

import api from 'api'

const FETCH_REQUEST = 'realhub/orderItemTask/FETCH_REQUEST'
const FETCH_SUCCESS = 'realhub/orderItemTask/FETCH_SUCCESS'
const FETCH_FAILURE = 'realhub/orderItemTask/FETCH_FAILURE'

const CREATE_REQUEST = 'realhub/orderItemTask/CREATE_REQUEST'
const CREATE_SUCCESS = 'realhub/orderItemTask/CREATE_SUCCESS'
const CREATE_FAILURE = 'realhub/orderItemTask/CREATE_FAILURE'

const UPDATE_REQUEST = 'realhub/orderItemTask/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realhub/orderItemTask/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realhub/orderItemTask/UPDATE_FAILURE'

const DELETE_REQUEST = 'realhub/orderItemTask/DELETE_REQUEST'
const DELETE_SUCCESS = 'realhub/orderItemTask/DELETE_SUCCESS'
const DELETE_FAILURE = 'realhub/orderItemTask/DELETE_FAILURE'

// Initial State
const initialState = {
  creating: false,
  loaded: false,
  loading: false,
  loadedIds: [],
  errors: [],
  updating: false,
  deleting: false,
}

// Actions
export function fetchRequest(payload = {}){
  return {
    type: FETCH_REQUEST,
    loadedIds: payload.loadedIds,
  }
}

export function fetchSuccess(){
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors = []){
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest(){
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess(){
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors = []){
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest(){
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess(){
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors = []){
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest(){
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess(){
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors = []){
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Helper Functions
const handleError = errors => errors || ['Something went wrong']

// Action Creators

export function hydrateOrderItemTask(orderItemTask){
  return (dispatch) => {
    dispatch(fetchRequest())

    if (orderItemTask.id){
      const normalizedJson = normalize(orderItemTask, Schemas.ORDER_ITEM_TASK)
      dispatch(updateEntities(normalizedJson))
      dispatch(fetchSuccess())
    } else {
      dispatch(fetchFailure(['Could not hydrate undefined order item task.']))
    }
  }
}

export function loadOrderItemTasks(options){
  return (dispatch) => {
    dispatch(fetchRequest())

    const promise = api('/admin/tasks.json', options)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.ORDER_ITEM_TASK_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return json
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(fetchFailure(errors))
      })

    return promise
  }
}

export function loadOrderItemTask(task, options){
  return (dispatch, getState) => {
    const state = getState()
    const loadedIds = state.orderItemTasks.loadedIds ? state.orderItemTasks.loadedIds.slice() : []

    if (!loadedIds.includes(task.id)){
      loadedIds.push(task.id)
    }

    dispatch(fetchRequest({ loadedIds }))

    const promise = api(`/admin/tasks/${task.id}.json`, options)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.ORDER_ITEM_TASK)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return json
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(fetchFailure(errors))
      })

    return promise
  }
}

export function createOrderItemTask(task, options, callback){
  const config = {
    method: 'POST',
    body: JSON.stringify({
      task,
    }),
  }

  return (dispatch) => {
    dispatch(createRequest())

    const promise = api('/admin/tasks.json', options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.ORDER_ITEM_TASK)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        if (callback) callback(json)

        return json
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(createFailure(errors))
      })

    return promise
  }
}

export function updateOrderItemTask(task, options, callback){
  const config = {
    method: 'PUT',
    body: JSON.stringify({
      task,
    }),
  }

  return (dispatch) => {
    dispatch(updateRequest())

    const promise = api(`/admin/tasks/${task.id}.json`, options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.ORDER_ITEM_TASK)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        if (callback) callback(json)

        return json
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(updateFailure(errors))
      })

    return promise
  }
}

export function updateOrderItemTaskSort(taskIds, options){
  const config = {
    method: 'PUT',
    body: JSON.stringify({
      task_ids: taskIds,
    }),
  }

  return (dispatch, getState) => {
    // Set Loading
    dispatch(updateRequest())

    const promise = api('/admin/tasks/update_sort_order.json', options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const { updated_ids } = json
        const state = getState()

        const updatedTasks = []
        updated_ids.forEach((id, index) => {
          const task = {
            ...state.entities.orderItemTasks[id],
            sort_order: index,
          }

          updatedTasks.push(task)
        })

        const normalizedJson = normalize(updatedTasks, Schemas.ORDER_ITEM_TASK_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return json
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(updateFailure(errors))
      })

    return promise
  }
}

export function deleteOrderItemTask(task, callback){
  const config = {
    method: 'DELETE',
  }

  return (dispatch) => {
    dispatch(deleteRequest())

    const promise = api(`/admin/tasks/${task.id}.json`, {}, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }
        const normalizedJson = normalize(json, Schemas.ORDER_ITEM_TASK)
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        if (callback) callback(json)

        return json
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(deleteFailure(errors))
      })
    return promise
  }
}

export function completeOrderItemTask(task, options, callback){
  const config = {
    method: 'PUT',
    body: JSON.stringify({
      task,
    }),
  }

  return (dispatch) => {
    dispatch(updateRequest())

    const promise = api(`/admin/tasks/${task.task_id}/complete.json`, options, config)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.ORDER_ITEM_TASK)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        if (callback) callback(json)

        return json
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(updateFailure(errors))
      })

    return promise
  }
}

export function searchOrderItemTasks(options, callback){
  return (dispatch) => {
    dispatch(fetchRequest())

    const promise = api('/admin/tasks/search.json', options)
      .then(response => response.json().then(json => ({ json, response })))
      .then(({ json, response }) => {
        if (!response.ok){
          return Promise.reject(json)
        }

        const normalizedJson = normalize(json, Schemas.ORDER_ITEM_TASK_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess(normalizedJson))

        if (callback) callback(json)

        return json
      })
      .catch((json) => {
        const errors = handleError(json.errors)
        dispatch(fetchFailure(errors))
      })

    return promise
  }
}

// Reducer
export default function reducer(state = initialState, action = {}){
  switch (action.type){
    case FETCH_REQUEST:
      return Object.assign({}, state, {
        loading: true,
        loadedIds: action.loadedIds || state.loadedIds,
      })
    case FETCH_SUCCESS:
      return Object.assign({}, state, {
        loading: false,
        loaded: true,
        errors: [],
      })
    case FETCH_FAILURE:
      return Object.assign({}, state, {
        loading: false,
        loaded: false,
        errors: action.errors,
      })
    case CREATE_REQUEST:
      return Object.assign({}, state, {
        creating: true,
      })
    case CREATE_SUCCESS:
      return Object.assign({}, state, {
        creating: false,
        errors: [],
      })
    case CREATE_FAILURE:
      return Object.assign({}, state, {
        creating: false,
        errors: action.errors,
      })
    case UPDATE_REQUEST:
      return Object.assign({}, state, {
        updating: true,
      })
    case UPDATE_SUCCESS:
      return Object.assign({}, state, {
        updating: false,
        errors: [],
      })
    case UPDATE_FAILURE:
      return Object.assign({}, state, {
        updating: false,
        errors: action.errors,
      })
    case DELETE_REQUEST:
      return Object.assign({}, state, {
        deleting: true,
      })
    case DELETE_SUCCESS:
      return Object.assign({}, state, {
        deleting: false,
        errors: [],
      })
    case DELETE_FAILURE:
      return Object.assign({}, state, {
        deleting: false,
        errors: action.errors,
      })
    default:
      return state
  }
}
